import api from '@/lib/api';

export type ReminderUrgency = 'low' | 'medium' | 'high' | 'urgent';

export interface Reminder {
  id: string;
  ticket_id: string;
  organization_id: string;
  title: string;
  description: string | null;
  reminder_date: string;
  urgency: ReminderUrgency;
  color: string;
  notify_before_minutes: number;
  is_completed: boolean;
  completed_at: string | null;
  completed_by: string | null;
  created_at: string;
  updated_at: string;
  created_by_name?: string;
  created_by_email?: string;
  completed_by_name?: string;
  ticket_subject?: string;
  ticket_number?: string;
}

export interface CreateReminderData {
  ticketId: string;
  title: string;
  description?: string;
  reminderDate: string | Date;
  urgency?: ReminderUrgency;
  color?: string;
  notifyBeforeMinutes?: number;
}

export interface UpdateReminderData {
  title?: string;
  description?: string;
  reminderDate?: string | Date;
  urgency?: ReminderUrgency;
  color?: string;
  notifyBeforeMinutes?: number;
  isCompleted?: boolean;
}

export interface ReminderDateRangeParams {
  startDate: string | Date;
  endDate: string | Date;
  ticketId?: string;
}

class RemindersService {
  /**
   * Create a new reminder
   */
  async createReminder(data: CreateReminderData): Promise<Reminder> {
    const response = await api.post('/reminders', data);
    return response.data.data;
  }

  /**
   * Get all reminders for a specific ticket
   */
  async getRemindersByTicket(ticketId: string): Promise<Reminder[]> {
    const response = await api.get(`/reminders/ticket/${ticketId}`);
    return response.data.data;
  }

  /**
   * Get reminders by date range
   */
  async getRemindersByDateRange(params: ReminderDateRangeParams): Promise<Reminder[]> {
    const response = await api.get('/reminders/date-range', { params });
    return response.data.data;
  }

  /**
   * Get a specific reminder by ID
   */
  async getReminderById(id: string): Promise<Reminder> {
    const response = await api.get(`/reminders/${id}`);
    return response.data.data;
  }

  /**
   * Update a reminder
   */
  async updateReminder(id: string, data: UpdateReminderData): Promise<Reminder> {
    const response = await api.put(`/reminders/${id}`, data);
    return response.data.data;
  }

  /**
   * Delete a reminder (soft delete)
   */
  async deleteReminder(id: string): Promise<void> {
    await api.delete(`/reminders/${id}`);
  }

  /**
   * Mark reminder as completed
   */
  async completeReminder(id: string): Promise<Reminder> {
    const response = await api.post(`/reminders/${id}/complete`);
    return response.data.data;
  }

  /**
   * Mark reminder as not completed
   */
  async uncompleteReminder(id: string): Promise<Reminder> {
    const response = await api.post(`/reminders/${id}/uncomplete`);
    return response.data.data;
  }
}

export default new RemindersService();
